import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import Skeleton from "@saleor/components/Skeleton";
import { PageErrorFragment } from "@saleor/fragments/types/PageErrorFragment";
import { PageheaderDetailFragment } from "@saleor/fragments/types/PageheaderDetailFragment";
import { sectionNames } from "@saleor/intl";
import { maybe, splitDateTime } from "@saleor/misc";
import { ListProps, TabListActions } from "@saleor/types";
import { PageHeaderLocatedEnum } from "@saleor/types/globalTypes";
import { concat } from "lodash-es";
import React from "react";
import { useIntl } from "react-intl";

import PageheaderColor from "../PageheaderColor";
import PageheaderDates from "../PageheaderDates";
import PageheaderDetails from "../PageheaderDetails/PageheaderDetails";
import PageheaderHistory from "../PageheaderHistory";
import PageheaderPageTypes from "../PageheaderPageTypes";
import PageheaderSummary from "../PageheaderSummary";
import TopheaderBackgroundColor from "../TopheaderBackgroundColor";

export interface PageheaderPageFormData {
  name: string;
  isDefault: boolean;
  isTop: boolean;
  countdown: boolean;
  pcCountdown: boolean;
  mbCountdown: boolean;
  languageOpt: boolean;
  pcContent: string;
  mobileContent: string;
  pcContentFormatted: string;
  mobileContentFormatted: string;
  located: PageHeaderLocatedEnum;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  mainColor: string;
  secondaryColor: string;
  pcRollingDuration: number;
  mbRollingDuration: number;
}

export interface PageheaderPageProps
  extends Pick<ListProps, Exclude<keyof ListProps, "onRowClick">>,
    TabListActions<"pageTypeListToolbar"> {
  pageheader: PageheaderDetailFragment | null;
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onBack: () => void;
  onDelete: () => void;
  onSubmit: (data: PageheaderPageFormData) => void;
  errors: PageErrorFragment[];
  title: string;
}

const PageheaderPage: React.FC<PageheaderPageProps> = ({
  pageheader,
  disabled,
  errors,
  saveButtonBarState,
  onBack,
  onDelete,
  onSubmit,
  onNextPage,
  onPreviousPage,
  pageInfo,
  pageTypeListToolbar,
  isChecked,
  selected,
  toggle,
  toggleAll,
  title
}) => {
  const intl = useIntl();
  const initialForm: PageheaderPageFormData =
    pageheader === null
      ? {
          name: "",
          isDefault: false,
          isTop: false,
          countdown: false,
          pcCountdown: false,
          mbCountdown: false,
          languageOpt: false,
          pcContent: "",
          mobileContent: "",
          pcContentFormatted: "-",
          mobileContentFormatted: "-",
          located: PageHeaderLocatedEnum.TOP,
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          mainColor: "",
          secondaryColor: "",
          pcRollingDuration: 0,
          mbRollingDuration: 0
        }
      : {
          name: maybe(() => pageheader.name, ""),
          isDefault: maybe(() => pageheader.isDefault, false),
          isTop: maybe(() => pageheader.isTop, false),
          countdown: maybe(() => pageheader.countdown, false),
          pcCountdown: maybe(() => pageheader.pcCountdown, false),
          mbCountdown: maybe(() => pageheader.mbCountdown, false),
          languageOpt: maybe(() => pageheader.languageOpt, false),
          pcContent: maybe(() => pageheader.pcContent, ""),
          mobileContent: maybe(() => pageheader.mobileContent, ""),
          pcContentFormatted: maybe(() => pageheader.pcContentFormatted, "-"),
          mobileContentFormatted: maybe(
            () => pageheader.mobileContentFormatted,
            "-"
          ),
          located: maybe(() => pageheader.located, PageHeaderLocatedEnum.TOP),
          startDate: splitDateTime(maybe(() => pageheader.startDate, "")).date,
          startTime: splitDateTime(maybe(() => pageheader.startDate, "")).time,
          endDate: splitDateTime(maybe(() => pageheader.endDate, "")).date,
          endTime: splitDateTime(maybe(() => pageheader.endDate, "")).time,
          mainColor: maybe(() => pageheader.mainColor, ""),
          secondaryColor: maybe(() => pageheader.secondaryColor, ""),
          pcRollingDuration: maybe(() => pageheader.pcRollingDuration, 0),
          mbRollingDuration: maybe(() => pageheader.mbRollingDuration, 0)
        };

  const handleSubmit = (data: PageheaderPageFormData) =>
    onSubmit({
      ...data
    });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.pageheaders)}
          </AppHeader>
          <PageHeader
            title={
              pageheader === null
                ? intl.formatMessage({
                    defaultMessage: "Create New Pageheader",
                    description: "create new pageheader"
                  })
                : maybe(() => concat(pageheader.name))
            }
          />
          <Grid>
            <div>
              <PageheaderDetails
                errors={errors}
                data={data}
                disabled={disabled}
                onChange={change}
                title={title}
              />
              <CardSpacer />
              {data.located === PageHeaderLocatedEnum.CHECKOUT ? (
                <>
                  <PageheaderColor
                    mainColor={data.mainColor}
                    secondaryColor={data.secondaryColor}
                    disabled={disabled}
                    onChange={change}
                  />
                </>
              ) : (
                <>
                  {data.located === PageHeaderLocatedEnum.TOP && (
                    <>
                      <TopheaderBackgroundColor
                        mainColor={data.mainColor}
                        secondaryColor={data.secondaryColor}
                        disabled={disabled}
                        onChange={change}
                      />
                      <CardSpacer />
                    </>
                  )}
                  <PageheaderDates
                    data={data}
                    disabled={disabled}
                    errors={errors}
                    onChange={change}
                  />
                  <CardSpacer />
                  {pageheader !== null ? (
                    <PageheaderPageTypes
                      disabled={disabled}
                      onNextPage={onNextPage}
                      onPreviousPage={onPreviousPage}
                      pageInfo={pageInfo}
                      pageheader={pageheader}
                      isChecked={isChecked}
                      selected={selected}
                      toggle={toggle}
                      toggleAll={toggleAll}
                      toolbar={pageTypeListToolbar}
                    />
                  ) : (
                    <Skeleton />
                  )}
                  <CardSpacer />
                  <PageheaderHistory history={pageheader?.events} />
                </>
              )}
            </div>
            <div>
              <PageheaderSummary data={data} />
            </div>
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            state={saveButtonBarState}
            onCancel={onBack}
            onSave={submit}
            onDelete={pageheader === null ? undefined : onDelete}
          />
        </Container>
      )}
    </Form>
  );
};
export default PageheaderPage;
